<template>
  <div class="pay-ranges-list-page">
    <div class="prlp-toolbar">
      <SearchBar
        type="button"
        :ghostText="searchPlaceholder"
        :btnText="addButtonText"
        :add="true"
        :label="filterLabel"
        :length="state.filters.values.length"
        :chipText="chipText"
        :valueSearch="state.filters.searchText"
        @onClick="onSearchBarClick"
        @onSearch="onSearch"
        @removeFilter="removeFilter"
      />
    </div>

    <div v-if="state.asOf.enabled || showReferenceCurrencyToggle" class="prlp-options">
      <div v-if="state.asOf.enabled" class="prlp-asof">
        <AsOfDateNavigator
          label="Show version as of"
          today-text="Today"
          :today="asOfIsToday"
          :as-of-date="asOfDate"
          @reset-to-today="resetAsOfToToday"
          @open-date-picker="openAsOfPicker"
        />
        <DatePicker
          v-if="state.asOf.pickerOpen"
          :isDatePickerOpen="state.asOf.pickerOpen"
          :value="asOfDate"
          :modal="true"
          @onApplyDate="applyAsOfDate"
          @onClose="closeAsOfPicker"
        />
      </div>

      <p
        v-if="state.asOf.enabled && resultCountLabel"
        class="prlp-count"
        role="status"
        aria-live="polite"
      >
        {{ resultCountLabel }}
      </p>

      <div v-if="showReferenceCurrencyToggle" class="prlp-toggle">
        <SwitchButton
          :label="referenceCurrencyToggleLabel"
          containerWidth="auto"
          left="0px"
          :value="state.showReferenceCurrency"
          @input="state.showReferenceCurrency = !!$event"
        />
      </div>
    </div>

    <PayRangesTable
      :key="state.tableKey"
      :rows="state.rows"
      :page-info="pageInfo"
      :show-reference-currency="state.showReferenceCurrency"
      :loading="state.loading"
      :error="state.asOf.enabled && state.loadError"
      :version-history="state.asOf.enabled"
      :has-active-search-or-filters="hasActiveSearchOrFilters"
      :clear-filter-label="clearFilterLabel"
      @row-action="onRowAction"
      @open-chatter="onOpenChatter"
      @open-detail="onOpenDetail"
      @open-positions="onOpenPositions"
      @retry="fetchRows"
      @page-change="onPageChange"
      @clear-filters="clearAllFilters"
    />

    <PayRangeTableFilterModal
      :value="state.filters.showFilterDialog"
      :initial-statuses="filterModalInitialStatuses"
      :initial-include-archived="filterModalInitialIncludeArchived"
      :initial-pay-basis="filterModalInitialPayBasis"
      :initial-work-mode="filterModalInitialWorkMode"
      :initial-currency="filterModalInitialCurrency"
      :initial-country="filterModalInitialCountry"
      @input="state.filters.showFilterDialog = $event"
      @close="state.filters.showFilterDialog = false"
      @apply="applyFilterModal"
    />

    <PayRangeModal
      :open="state.createModal.show"
      :mode="state.createModal.mode"
      :payRangeId="state.createModal.payRangeId"
      :stepNumber="state.createModal.stepNumber"
      :initialValues="state.createModal.initialValues"
      :countryItems="props.operatingCountries"
      :currencyItems="currencyItems"
      :saveDraft="handleSaveDraft"
      :suggestRange="handleSuggestRange"
      @onClose="closeCreateModal"
      @closeAddDialog="closeCreateModal"
    />

    <v-dialog
      :value="state.retireDialog.show"
      max-width="420"
      @click:outside="closeRetire"
      @keydown.esc="closeRetire"
    >
      <v-card class="prlp-retire-card">
        <v-card-title>Retire pay range</v-card-title>
        <v-card-text>
          <p class="prlp-retire-copy">
            Retire <strong>{{ state.retireDialog.name }}</strong> on:
          </p>
          <v-text-field
            v-model="state.retireDialog.retireOn"
            type="date"
            dense
            outlined
            hide-details
            :min="state.retireDialog.minRetireOn || undefined"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeRetire">Cancel</v-btn>
          <v-btn color="primary" @click="confirmRetire">Retire</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      :dialog="state.deleteDialog.show"
      icon="delete.svg"
      @confirm="confirmDeleteDraft"
      @cancel="closeDeleteDraft"
    >
      <template #title>Delete draft</template>
      <template #content>
        Delete <strong>{{ state.deleteDialog.name || "this draft pay range" }}</strong>?
        This cannot be undone.
      </template>
    </ConfirmationDialog>

    <ConfirmationDialog
      :dialog="state.versionDialog.show"
      :icon="versionDialogCopy.icon"
      :confirm-label="versionDialogCopy.confirmLabel"
      @confirm="confirmVersionDialog"
      @cancel="closeVersionDialog"
    >
      <template #title>{{ versionDialogCopy.title }}</template>
      <template #content>
        {{ versionDialogCopy.lead }}
        <strong>{{ state.versionDialog.name || "this pay range" }}</strong>?
        {{ versionDialogCopy.consequence }}
      </template>
    </ConfirmationDialog>

    <ChatterDrawer
      :drawer="state.chatter.show"
      :iconName="state.chatter.iconName"
      :type="state.chatter.typeName"
      :typeValue="state.chatter.typeValue"
      :listData="state.chatter.listData"
      object-type="payrange"
      :object-id="state.chatter.listData?.id ?? null"
      :chatterList="state.chatter.items"
      :totalCheckpointChatter="state.chatter.total"
      @closeDrawer="closeChatterDrawer"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import AsOfDateNavigator from "@components/AsOfDateNavigator/AsOfDateNavigator.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import PayRangesTable from "@components/PayRangesTable/PayRangesTable.vue";
import PayRangeModal from "@components/PayRangeModal/PayRangeModal.vue";
import PayRangeTableFilterModal from "@components/PayRangeTableFilterModal/PayRangeTableFilterModal.vue";
import ConfirmationDialog from "@components/ResponsibilitiesPicker/ConfirmationDialog.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { suggestPayRange } from "@/api/payRangeAPI";
import { usePayRangesListPageState } from "./composables/usePayRangesListPageState";

const emit = defineEmits(["new-version", "edit-draft"]);

const handleSuggestRange = (seed, options) => suggestPayRange(seed, options);

const props = defineProps({
  /** Number of active operating currencies on the account (OC Rule 17). When exactly 1,
   *  the "Show amounts in reference currency" toggle is hidden — there is nothing to convert
   *  between. Null (unknown) renders the toggle as before for consumers that do not pass it. */
  activeOperatingCurrencyCount: {
    type: Number,
    default: null,
  },
  /** Reference (base) currency code of the account, e.g. "USD" (ER-11233). Shown inside the
   *  toggle label so users know which currency amounts are converted into. Null (unknown)
   *  falls back to the generic label for consumers that do not pass it. */
  referenceCurrencyCode: {
    type: String,
    default: null,
  },
  /** Active operating countries (ER-11872) — merged onto the global country catalog inside
   *  PayRangeModal for `operatingCountryId`; the Location picker still lists all countries. */
  operatingCountries: {
    type: Array,
    default: null,
  },
  /** Active operating currencies (ER-11873) — picker list, id merge, and single-currency UX. */
  currencyItems: {
    type: Array,
    default: () => [],
  },
});

/** OC Rule 17 — hide the reference-currency toggle when the account has exactly one active
 *  operating currency (the reference currency itself). */
const showReferenceCurrencyToggle = computed(() => props.activeOperatingCurrencyCount !== 1);

/** ER-11233 — "Show amounts in <CODE> (your reference currency)" once the account's reference
 *  currency code is known; the generic label otherwise. */
const referenceCurrencyToggleLabel = computed(() => {
  if (props.referenceCurrencyCode) {
    return `Show amounts in ${props.referenceCurrencyCode} (your reference currency)`;
  }
  return "Show amounts in reference currency";
});

const {
  state,
  pageInfo,
  resultCountLabel,
  chipText,
  filterLabel,
  filterModalInitialStatuses,
  filterModalInitialIncludeArchived,
  filterModalInitialPayBasis,
  filterModalInitialWorkMode,
  filterModalInitialCurrency,
  filterModalInitialCountry,
  hasActiveSearchOrFilters,
  clearFilterLabel,
  fetchRows,
  asOfDate,
  asOfIsToday,
  loadAsOfFlag,
  openAsOfPicker,
  closeAsOfPicker,
  applyAsOfDate,
  resetAsOfToToday,
  loadFiltersOnReload,
  applyFilterModal,
  removeFilter,
  clearAllFilters,
  onSearch,
  onPageChange,
  openCreateModal,
  closeCreateModal,
  handleSaveDraft,
  onRowAction,
  onOpenChatter,
  onOpenDetail,
  onOpenPositions,
  closeChatterDrawer,
  loadMoreChatter,
  closeRetire,
  confirmRetire,
  closeDeleteDraft,
  confirmDeleteDraft,
  closeVersionDialog,
  confirmVersionDialog,
  handleVersionRejection,
} = usePayRangesListPageState({ emit });

const VERSION_DIALOG_COPY = {
  void: {
    icon: "delete.svg",
    confirmLabel: "Void",
    title: "Void next version",
    lead: "Void the next version of",
    consequence: "It will stay in history as Voided and can no longer be used.",
  },
  publish: {
    icon: "Add_Icon_gray.svg",
    confirmLabel: "Publish",
    title: "Publish draft",
    lead: "Publish the draft version of",
    consequence: "It takes effect on its effective date.",
  },
  discard: {
    icon: "delete.svg",
    confirmLabel: "Discard",
    title: "Discard draft",
    lead: "Discard the draft version of",
    consequence: "This cannot be undone.",
  },
};

const versionDialogCopy = computed(
  () => VERSION_DIALOG_COPY[state.versionDialog.kind] || VERSION_DIALOG_COPY.discard
);

/** idea-MDP-429 — Figma toolbar copy; the legacy copy stays while the flag is off. */
const searchPlaceholder = computed(() =>
  state.asOf.enabled ? "Search by code, job family or level" : "Search by name or code"
);
const addButtonText = computed(() =>
  state.asOf.enabled ? "Add Pay Range" : "Create Draft Pay Range"
);

defineExpose({ handleVersionRejection, fetchRows });

function onSearchBarClick(type) {
  if (type === "add") {
    openCreateModal();
    return;
  }
  state.filters.showFilterDialog = true;
}

onMounted(async () => {
  await loadFiltersOnReload();
  await loadAsOfFlag();
  await fetchRows();
});
</script>

<style scoped>
.pay-ranges-list-page {
  width: 100%;
  padding: 8px 0 24px;
}

.prlp-toolbar {
  margin-bottom: 8px;
}

.prlp-options {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.prlp-count {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.prlp-toggle {
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  padding-right: 4px;
}

.prlp-retire-card {
  padding: 8px;
}

.prlp-retire-copy {
  margin: 0 0 12px;
  color: #475467;
  font-size: 14px;
}
</style>
